import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Flyout } from '@els/els-react--flyout';
import InnerHTML from '@Components/InnerHTML/InnerHTML';
import ConditionsSubstanceLabel from './ConditionsSubstanceLabel';

// The Flyout asks to open on hover/focus; only then do we measure truncation and
// allow it to open. This avoids layout-observer feedback loops from style changes.
function ChartLabel({ item, anchorId = undefined, onBeforeNavigate = () => null }) {
  const textRef = useRef(null);
  const [isTooltipOpen, setTooltipOpen] = useState(false);
  const text = item?.name ?? '';

  const handleToggle = useCallback((nextOpen) => {
    if (!nextOpen) {
      setTooltipOpen(false);
      return;
    }
    const el = textRef.current;
    setTooltipOpen(Boolean(el && el.scrollWidth > el.clientWidth));
  }, []);

  const labelNode = (
    <ConditionsSubstanceLabel
      item={item}
      id={anchorId}
      isChartLink
      dataE2e="e2e-conditions-summary-substance"
      className={`rx-mini-bar__label${item?.id ? ' rx-mini-bar__label--link' : ''}`}
      textClassName="rx-mini-bar__label-text"
      textRef={textRef}
      onBeforeNavigate={() => onBeforeNavigate(anchorId)}
    />
  );

  return (
    <span className="rx-mini-bar__label-cell">
      <Flyout
        inline
        isControlled
        isOpen={isTooltipOpen}
        placement="top-start"
        width="max-content"
        theme="simple"
        trigger="hover"
        shouldCloseOnESC
        onToggle={handleToggle}
        flyout={isTooltipOpen ? <InnerHTML tag="span" className="u-els-font-size-eyebrow" html={text} /> : null}>
        {labelNode}
      </Flyout>
    </span>
  );
}

ChartLabel.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    databaseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  anchorId: PropTypes.string,
  onBeforeNavigate: PropTypes.func,
};

function ConditionsSummaryChart({ title, data, idPrefix = undefined, onBeforeNavigate = () => null }) {
  if (!data.length) return null;
  const max = Math.max(...data.map((d) => d.count));

  return (
    <ul className="rx-summary-card__list u-els-padding-bottom" aria-label={title}>
      {data.map((d, index) => {
        const progressValue = max > 0 ? Math.round((d.count / max) * 100) : 0;
        const anchorId = idPrefix ? `${idPrefix}-${index}` : undefined;

        return (
          // eslint-disable-next-line react/no-array-index-key -- the backend can report the same substance id more than once within a tab; index guarantees uniqueness
          <li key={`${d.id ?? d.name}-${index}`} className="rx-mini-bar">
            <ChartLabel item={d} anchorId={anchorId} onBeforeNavigate={onBeforeNavigate} />
            <progress
              className="c-els-progress-bar rx-mini-bar__progress"
              max="100"
              value={progressValue}
              aria-label={`${d.name} count`}
              aria-valuetext={`${d.count} reactions; ${progressValue}% of highest count`}
            />
            <span className="rx-mini-bar__count">{d.count}</span>
          </li>
        );
      })}
    </ul>
  );
}

ConditionsSummaryChart.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      count: PropTypes.number,
    })
  ).isRequired,
  idPrefix: PropTypes.string,
  onBeforeNavigate: PropTypes.func,
};

export default ConditionsSummaryChart;
